import {Button} from '@wandb/components';
import React, {useCallback, useState} from 'react';

import {
  trackMarimoCreateNotebookPanelSettings,
  trackMarimoForkNotebookPanelSettings,
} from '../../../../services/analytics/productEvents';
import {recordRumAction} from '../../../../util/datadog/datadogRum';
import {ActiveNotebookBar} from './ActiveNotebookBar';
import {useMarimoSettingsStateContext} from './MarimoSettingsContextProvider';
import {SourceNotebookSelect} from './SourceNotebookSelect';
import {CREATE_NOTEBOOK_ID, useNotebookItems} from './useNotebookItems';
import {
  LOADING_STRINGS,
  useOnCreateNotebookClick,
  useOnForkNotebookClick,
} from './useOnCreateNotebookClick';

export const SourceNotebook = () => {
  const {config} = useMarimoSettingsStateContext();
  // We need an intermediate selected option state while we wait for the notebook to be created
  const [selectedOptionID, setSelectedOptionID] = useState<string | null>(
    config.notebookId ?? null
  );

  const [isCreatingNotebook, setIsCreatingNotebook] = useState(false);
  const [loadingString, setLoadingString] = useState(LOADING_STRINGS[0]);

  const onNotebookCreation = useCallback(
    (value: string | null) => {
      setSelectedOptionID(value);
      recordRumAction('panel_marimo_notebook_created', {
        notebookId: value as string,
      });
    },
    [setSelectedOptionID]
  );

  const onCreateNotebookClick = useOnCreateNotebookClick(
    setIsCreatingNotebook,
    setLoadingString,
    onNotebookCreation
  );
  const onCreateNotebookClickWithTracking = useCallback(async () => {
    trackMarimoCreateNotebookPanelSettings();
    await onCreateNotebookClick();
  }, [onCreateNotebookClick]);

  const onNotebookFork = useCallback(
    (value: string | null) => {
      setSelectedOptionID(value);
      recordRumAction('panel_marimo_notebook_forked', {
        notebookId: value as string,
      });
    },
    [setSelectedOptionID]
  );

  const onForkNotebookClick = useOnForkNotebookClick(
    selectedOptionID,
    setIsCreatingNotebook,
    setLoadingString,
    onNotebookFork
  );

  const onForkNotebookClickWithTracking = useCallback(async () => {
    if (selectedOptionID) {
      trackMarimoForkNotebookPanelSettings({
        notebookId: selectedOptionID,
      });
    }
    await onForkNotebookClick();
  }, [onForkNotebookClick, selectedOptionID]);

  const {
    notebookItems,
    loading,
    onInputChange,
    onMenuScrollToBottom,
    onBlur,
    sourceNotebook,
  } = useNotebookItems(selectedOptionID);

  const isChangingNotebook =
    selectedOptionID != null &&
    (selectedOptionID !== config.notebookId || isCreatingNotebook);
  let actions = null;
  if (isChangingNotebook) {
    let buttonLabel = null;
    if (isCreatingNotebook) {
      buttonLabel = loadingString;
    } else if (selectedOptionID === CREATE_NOTEBOOK_ID) {
      buttonLabel = 'Create notebook';
    } else {
      buttonLabel = 'Copy notebook';
    }

    const onPress =
      selectedOptionID === CREATE_NOTEBOOK_ID
        ? onCreateNotebookClickWithTracking
        : onForkNotebookClickWithTracking;

    actions = (
      <Button
        variant="primary"
        endIcon={isCreatingNotebook ? 'loading' : 'open-new-tab'}
        isDisabled={isCreatingNotebook}
        onPress={onPress}>
        {buttonLabel}
      </Button>
    );
  } else if (sourceNotebook != null) {
    actions = <ActiveNotebookBar sourceNotebook={sourceNotebook} />;
  }

  return (
    <div className="flex flex-col gap-y-12">
      <SourceNotebookSelect
        selectedOptionID={selectedOptionID}
        setSelectedOptionID={setSelectedOptionID}
        notebookItems={notebookItems}
        loading={loading}
        onInputChange={onInputChange}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onBlur={onBlur}
      />
      {actions}
    </div>
  );
};
